import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Check, Zap, Crown, Sparkles } from 'lucide-react';

const BillingPage = () => {
  const { api, user, updateUser } = useAuth();
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(null);

  useEffect(() => {
    fetchBilling();
  }, []);

  const fetchBilling = async () => {
    try {
      const response = await api.get('/billing');
      setBilling(response.data);
    } catch (error) {
      console.error('Error fetching billing:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async (planId) => {
    setUpgrading(planId);
    try {
      await api.post(`/billing/upgrade?plan=${planId}`);
      updateUser({ ...user, plan: planId });
      toast.success(`Plan mis à jour vers ${planId}`);
      fetchBilling();
    } catch (error) {
      console.error('Error upgrading:', error);
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setUpgrading(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const plans = billing?.plans || [];
  const currentPlan = billing?.currentPlan || 'STARTER';

  return (
    <div className="space-y-6 max-w-4xl" data-testid="billing-page">
      <div>
        <h1 className="text-3xl font-bold font-['Manrope']">Abonnement</h1>
        <p className="text-muted-foreground mt-1">
          Gérez votre plan et vos options
        </p>
      </div>

      {/* Current plan */}
      <Card className="border-primary">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Votre plan actuel</CardTitle>
              <CardDescription>
                Vous êtes actuellement sur le plan {currentPlan}
              </CardDescription>
            </div>
            <Badge variant="outline" className="text-lg px-4 py-1">
              {currentPlan}
            </Badge>
          </div>
        </CardHeader>
      </Card>

      {/* Plans */}
      <div className="grid gap-6 md:grid-cols-3">
        {plans.map((plan) => (
          <PlanCard 
            key={plan.id}
            plan={plan}
            currentPlan={currentPlan}
            upgrading={upgrading}
            onUpgrade={handleUpgrade}
          />
        ))}
      </div>

      {/* Info */}
      <Card>
        <CardHeader>
          <CardTitle>Informations</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-sm text-muted-foreground">
          <p>
            <strong>Changement de plan :</strong> Les changements prennent effet immédiatement. 
            Vous serez facturé au prorata pour le reste de la période.
          </p>
          <p>
            <strong>Sans engagement :</strong> Vous pouvez changer ou annuler votre plan à tout moment.
          </p>
          <p>
            <strong>Note :</strong> En mode démo, les changements de plan sont simulés et ne génèrent pas de facturation réelle.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

const PlanCard = ({ plan, currentPlan, upgrading, onUpgrade }) => {
  const isCurrent = plan.id === currentPlan;
  const isDowngrade = 
    (currentPlan === 'PREMIUM' && plan.id !== 'PREMIUM') ||
    (currentPlan === 'PRO' && plan.id === 'STARTER');

  const getIcon = (planId) => {
    switch (planId) {
      case 'STARTER': return <Zap className="w-5 h-5" />;
      case 'PRO': return <Crown className="w-5 h-5" />;
      case 'PREMIUM': return <Sparkles className="w-5 h-5" />;
      default: return null;
    }
  };

  const features = plan.features || [];
  
  return (
    <Card 
      className={`relative ${plan.recommended ? 'border-primary shadow-lg' : ''} ${isCurrent ? 'ring-2 ring-primary' : ''}`}
      data-testid={`billing-plan-${plan.id.toLowerCase()}`}
    >
      {plan.recommended && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <Badge className="bg-primary">Recommandé</Badge>
        </div>
      )}
      
      {isCurrent && (
        <div className="absolute -top-3 right-4">
          <Badge variant="outline" className="bg-background">Plan actuel</Badge>
        </div>
      )}
      
      <CardHeader>
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary">
            {getIcon(plan.id)}
          </div>
          <CardTitle>{plan.name}</CardTitle>
        </div>
        <div className="pt-2">
          <span className="text-4xl font-bold">{plan.price}€</span>
          <span className="text-muted-foreground">/mois</span>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        <ul className="space-y-3">
          {features.map((feature, idx) => (
            <li key={idx} className="flex items-start gap-2">
              <Check className="w-5 h-5 text-primary shrink-0 mt-0.5" />
              <span className="text-sm">{feature}</span>
            </li>
          ))}
        </ul>
        
        <Button
          className="w-full rounded-full"
          variant={isCurrent ? 'outline' : 'default'}
          disabled={isCurrent || upgrading === plan.id}
          onClick={() => onUpgrade(plan.id)}
          data-testid={`upgrade-to-${plan.id.toLowerCase()}`}
        >
          {upgrading === plan.id 
            ? 'Mise à jour...' 
            : isCurrent 
            ? 'Plan actuel' 
            : isDowngrade 
            ? 'Rétrograder'
            : 'Choisir ce plan'
          }
        </Button>
      </CardContent>
    </Card>
  );
};

export default BillingPage;
